import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { del, get, patch, post } from "../lib/api";
import { useDebounced, useDocumentTitle, useLoad, useQueryState, useOnChange, useRememberListUrl } from "../lib/hooks";
import { addDays, formatDay, plural, relativeDay } from "../lib/format";
import { SIGNAL_TYPES, signalTypeIcon, signalTypeLabel, typeSummary, urlHost } from "../lib/leads";
import { todayISO } from "../../helpers/datetime";
import { Avatar, Badge, Button, Card, Empty, ErrorBox, Input, Loading, Menu, MenuItem, PageHead, Pager, SearchInput, Segment, Select, Toggle } from "../ui";
import { useShell } from "../shell/context";

const PAGE_SIZE = 20;
const PER_GROUP = 10;

const RANGES = [
    { value: "", label: "Any time" },
    { value: "7", label: "7 days" },
    { value: "30", label: "30 days" },
    { value: "90", label: "90 days" },
    { value: "custom", label: "Dates" },
];

export default function Signals() {
    useDocumentTitle("Signals");
    useRememberListUrl();
    const { version, openSignal } = useShell();
    const location = useLocation();
    const history = useHistory();
    const params = new URLSearchParams(location.search);
    const companyId = params.get("company_id") || "";
    const highlight = params.get("highlight") || "";
    const [q, setQ] = useQueryState("q", "");
    const [type, setType] = useQueryState("type", "");
    const [notActed, setNotActed] = useQueryState("open_only", !companyId && !highlight);
    const [range, setRange] = useQueryState("range", "");
    const [since, setSince] = useQueryState("since", "");
    const [until, setUntil] = useQueryState("until", "");
    const [page, setPage] = useQueryState("page", 1);
    const dq = useDebounced(q.trim(), 250);
    const today = todayISO();

    const sinceParam = range === "custom" ? since : range ? addDays(today, -Number(range)) : "";
    const untilParam = range === "custom" ? until : "";

    useOnChange([dq, type, notActed, sinceParam, untilParam, companyId], () => setPage(1));

    const groups = useLoad(
        () =>
            get("v1/crm/signals/by-company", {
                q: dq,
                type,
                acted_on: notActed ? "false" : undefined,
                since: sinceParam,
                until: untilParam,
                company_id: companyId,
                per_group: PER_GROUP,
                page,
                page_size: PAGE_SIZE,
            }),
        [dq, type, notActed, sinceParam, untilParam, companyId, page, version]
    );

    const clearCompany = () => {
        params.delete("company_id");
        params.delete("highlight");
        history.replace({ pathname: location.pathname, search: params.toString() });
    };
    const showCompany = (id) => {
        params.set("company_id", id);
        params.delete("highlight");
        history.push({ pathname: location.pathname, search: params.toString() });
    };

    const data = groups.data;
    const rows = (data && data.data) || [];
    const companyName = companyId && rows[0] && rows[0].company_id === companyId ? rows[0].company_name : "One company";
    const signalCount = rows.reduce((n, g) => n + g.count, 0);

    return (
        <>
            <PageHead
                crumb="CRM"
                title="Signals"
                sub={data ? `${plural(data.count, "company", "companies")} with ${notActed ? "signals not acted on" : "signals"}${data.max_page > 1 ? "" : ` · ${plural(signalCount, "signal")}`}` : " "}
                actions={<Button variant="primary" icon="ri-add-line" onClick={() => openSignal(companyId && rows[0] ? { company: { id: companyId, name: rows[0].company_name } } : {})}>Add signal</Button>}
            />
            <Card noBody>
                <div className="hz-filterbar">
                    <SearchInput value={q} onChange={setQ} placeholder="Filter by company or detail…" />
                    <Select value={type} onChange={(e) => setType(e.target.value)} placeholder="Any type" options={SIGNAL_TYPES} aria-label="Type" />
                    <Toggle on={notActed} onChange={setNotActed}>Not acted on</Toggle>
                    {companyId && (
                        <span className="hz-filter-chip">
                            <i className="ri-building-line"></i>
                            {companyName}
                            <button type="button" aria-label="Clear company filter" onClick={clearCompany}>
                                <i className="ri-close-line"></i>
                            </button>
                        </span>
                    )}
                </div>
                <div className="hz-filterbar">
                    <span className="hz-small hz-muted">Seen</span>
                    <Segment options={RANGES} value={range} onChange={setRange} />
                    {range === "custom" && (
                        <span className="hz-date-range">
                            <Input small type="date" value={since} max={until || today} onChange={(e) => setSince(e.target.value)} aria-label="Seen from" />
                            <span className="hz-muted">to</span>
                            <Input small type="date" value={until} min={since || undefined} max={today} onChange={(e) => setUntil(e.target.value)} aria-label="Seen until" />
                        </span>
                    )}
                </div>
                <ErrorBox error={groups.error} onRetry={groups.reload} />
                {groups.loading && !data ? (
                    <Loading />
                ) : (
                    <>
                        {rows.length === 0 && !groups.error && (
                            <Empty>{notActed ? "Nothing waiting: every signal here has been acted on." : "No signals match these filters."}</Empty>
                        )}
                        {rows.map((g) => (
                            <Group key={g.company_id} group={g} today={today} highlight={highlight} onShowAll={() => showCompany(g.company_id)} single={!!companyId} />
                        ))}
                    </>
                )}
                {data && <Pager page={page} maxPage={data.max_page} count={data.count} onPage={setPage} label={data.count === 1 ? "company" : "companies"} />}
            </Card>
        </>
    );
}

function Group({ group, today, highlight, onShowAll, single }) {
    const { openSignal } = useShell();
    const [signals, setSignals] = useState(group.signals || []);
    useEffect(() => setSignals(group.signals || []), [group]);
    const update = (s) => setSignals((list) => list.map((x) => (x.id === s.id ? { ...x, ...s } : x)));
    const remove = (id) => setSignals((list) => list.filter((x) => x.id !== id));
    const notActed = signals.filter((s) => !s.acted_on).length + Math.max(0, group.not_acted_on - (group.signals || []).filter((s) => !s.acted_on).length);

    return (
        <div className="hz-sig-group">
            <div className="hz-sig-head">
                <Avatar name={group.company_name} />
                <div className="hz-spacer" style={{ minWidth: 0 }}>
                    <Link to={`/crm/companies/${group.company_id}`} className="hz-name">{group.company_name}</Link>
                    <div className="hz-small hz-muted">
                        {plural(group.count, "signal")}
                        {notActed > 0 ? ` · ${notActed} not acted on` : " · all acted on"} · latest {formatDay(group.latest_seen_on, { short: true, today })}
                        {group.count > 1 ? ` · ${typeSummary(group.types)}` : ""}
                    </div>
                </div>
                <Button size="sm" variant="ghost" icon="ri-add-line" onClick={() => openSignal({ company: { id: group.company_id, name: group.company_name } })}>Signal</Button>
            </div>
            {signals.map((s) => (
                <SignalRow key={s.id} signal={s} today={today} highlight={highlight === s.id} onChange={update} onRemoved={remove} />
            ))}
            {group.count > (group.signals || []).length && !single && (
                <div className="hz-sig-more">
                    <button type="button" className="hz-btn-link" onClick={onShowAll}>Show all {group.count} signals at {group.company_name}</button>
                </div>
            )}
        </div>
    );
}

function SignalRow({ signal: s, today, highlight, onChange, onRemoved }) {
    const { toast, bump, openLead, openSignal } = useShell();
    const [busy, setBusy] = useState(false);
    const rowRef = useRef(null);
    useEffect(() => {
        if (highlight && rowRef.current && rowRef.current.scrollIntoView) rowRef.current.scrollIntoView({ block: "center" });
    }, [highlight]);

    const setActed = async (on) => {
        setBusy(true);
        onChange({ id: s.id, acted_on: on });
        try {
            const res = await patch(`v1/crm/signals/${s.id}`, { acted_on: on });
            onChange(res);
            toast(on ? "Marked acted on." : "Marked not acted on.", {
                action: { label: "Undo", run: () => patch(`v1/crm/signals/${s.id}`, { acted_on: !on }).then(onChange).catch((e) => toast(e.message, { error: true })) },
            });
        } catch (e) {
            onChange({ id: s.id, acted_on: !on });
            toast(e.message, { error: true });
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        try {
            await del(`v1/crm/signals/${s.id}`);
            onRemoved(s.id);
            toast("Signal moved to the trash.", {
                icon: "ri-delete-bin-line",
                action: { label: "Undo", run: () => post(`v1/crm/trash/signals/${s.id}/restore`).then(bump).catch((e) => toast(e.message, { error: true })) },
            });
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    return (
        <div className={"hz-sig-row" + (s.acted_on ? " acted" : "") + (highlight ? " highlight" : "")} ref={rowRef}>
            <span className="hz-sig-icon"><i className={signalTypeIcon(s.type)}></i></span>
            <div className="hz-sig-main">
                <div className="hz-row-wrap" style={{ gap: 6 }}>
                    <span className="hz-strong">{signalTypeLabel(s.type)}</span>
                    <span className="hz-small hz-muted" title={formatDay(s.seen_on)}>seen {formatDay(s.seen_on, { short: true, today })} ({relativeDay(s.seen_on, today)})</span>
                    {s.source === "claude" && <Badge color="primary" title="Added by Claude">Claude</Badge>}
                    {s.lead_count > 0 && (
                        <Link to={`/crm/leads?view=list&company_id=${s.company_id}&label=${encodeURIComponent(s.company_name)}`} className="hz-small">
                            <i className="ri-funds-line"></i> {plural(s.lead_count, "lead")}
                        </Link>
                    )}
                </div>
                {s.detail && <div className="hz-sig-detail hz-pre">{s.detail}</div>}
                {s.source_url && (
                    <a className="hz-small" href={s.source_url} target="_blank" rel="noopener noreferrer">
                        <i className="ri-external-link-line"></i> {urlHost(s.source_url)}
                    </a>
                )}
            </div>
            <div className="hz-sig-actions">
                <Toggle on={s.acted_on} disabled={busy} onChange={setActed}>
                    <span className="hz-small">Acted on</span>
                </Toggle>
                <Button size="sm" variant="soft" icon="ri-funds-line" onClick={() => openLead({ signal: s })}>Create lead</Button>
                <Menu trigger={(open, toggle) => <Button size="sm" variant="ghost" iconOnly icon="ri-more-2-fill" title="More" onClick={toggle} />}>
                    <MenuItem onClick={() => openSignal({ signal: s, onSaved: onChange })}>Edit signal</MenuItem>
                    <MenuItem danger onClick={remove}>Delete signal</MenuItem>
                </Menu>
            </div>
        </div>
    );
}
